import { ISession } from "features/manage/sessions/ISession";
import { SessionsStore } from "features/manage/sessions/SessionsStore";
import { BaseManagePackageElement } from "features/manage/BaseManagePackageElement";
import { Package } from "features/packages/Package";
import { Routing } from "lib/Routing";
import { AppContextStore } from "features/application-context/AppContextStore";
import "./SessionsElement-style";

class SessionsElement extends BaseManagePackageElement {
    protected slug = "sessions";
    private sessions: ISession[] = [];

    protected async initialize() {
        this.sessions = await SessionsStore.instance.getAll();
    }

    protected setupDOM() {
        this.addEventListener("click", async event => {
            const target = <HTMLElement>event.target;
            if (!target.attributes) return;

            const action = target.getAttribute("data-action");
            const sessionId = target.getAttribute("data-session-id");

            if (action === "revoke" && sessionId) {
                target.setAttribute("disabled", "disabled");
                target.textContent = "Revoking...";
                await SessionsStore.instance.revoke(sessionId);
            }

            if (action === "revoke-others") {
                target.setAttribute("disabled", "disabled");
                target.textContent = "Revoking...";
                await SessionsStore.instance.revokeOthers();
            }
        });

        SessionsStore.instance.onSessionRevoked(id => {
            const row = this.querySelector(`[data-row-id="${id}"]`) as HTMLElement;
            if (row) {
                const btn = row.querySelector("button");
                if (btn) {
                    btn.setAttribute("disabled", "disabled");
                    btn.textContent = "Revoked";
                    btn.classList.add("revoked");
                }
                row.classList.add("revoked");
            }
        });

        SessionsStore.instance.onOtherSessionsRevoked(() => {
            const rows = Array.from(this.querySelectorAll("[data-row-id]")) as HTMLElement[];
            rows.forEach(row => {
                const isCurrent = row.getAttribute("data-current") === "true";
                if (!isCurrent) {
                    const btn = row.querySelector("button");
                    if (btn) {
                        btn.setAttribute("disabled", "disabled");
                        btn.textContent = "Revoked";
                        btn.classList.add("revoked");
                    }
                    row.classList.add("revoked");
                }
            });
            const revokeAllBtn = this.querySelector("[data-action='revoke-others']") as HTMLButtonElement;
            if (revokeAllBtn) {
                revokeAllBtn.setAttribute("disabled", "disabled");
                revokeAllBtn.textContent = "Done";
            }
        });
    }

    protected detailView = (_thePackage: Package) => `
        <div class="sessions-actions">
            <button class="power" data-action="revoke-others">Revoke all other sessions</button>
        </div>
        <div class="sessions-table">
            <div class="sessions-header">
                <span class="col-app">Application</span>
                <span class="col-permissions">Permissions</span>
                <span class="col-date">Last used</span>
                <span class="col-agent">Device</span>
                <span class="col-action">Action</span>
            </div>
            ${this.sessions.length === 0
                ? `<div class="sessions-empty">No active sessions found.</div>`
                : this.sessions.map(s => this.renderRow(s)).join("")}
        </div>
    `;

    private renderRow = (session: ISession) => `
        <div class="sessions-row ${session.isCurrent ? "current" : ""}" data-row-id="${session.id}" data-current="${session.isCurrent}">
            <span class="col-app">
                <span class="app-name">${this.escapeHtml(session.applicationName || "Unknown")}</span>
                ${session.isCurrent ? `<span class="badge-current">This device</span>` : ""}
            </span>
            <span class="col-permissions">
                ${this.renderScopeBadges(session.scopes)}
            </span>
            <span class="col-date">
                <span class="date-primary">${this.formatDate(session.lastUsedAt)}</span>
                <span class="date-secondary">Since ${this.formatDate(session.createdAt)}</span>
            </span>
            <span class="col-agent" title="${this.escapeHtml(session.userAgent)}">${this.escapeHtml(session.deviceName)}</span>
            <span class="col-action">
                <button class="power ${session.isCurrent ? "" : ""}"
                    data-action="revoke"
                    data-session-id="${session.id}"
                    ${session.isCurrent ? "disabled" : ""}>
                    ${session.isCurrent ? "Current" : "Revoke"}
                </button>
            </span>
        </div>
    `;

    private renderScopeBadges(scopes: string[]): string {
        if (!scopes || scopes.length === 0) return "";
        return scopes.map(scope => {
            const label = scope === "full" ? "Full access" : scope.replace(":", " ").toUpperCase();
            const cls = scope === "full" ? "badge-scope badge-scope--full" : "badge-scope badge-scope--limited";
            return `<span class="${cls}">${label}</span>`;
        }).join(" ");
    }

    private formatDate(iso: string): string {
        if (!iso) return "—";
        try {
            const d = new Date(iso);
            return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
        } catch {
            return iso;
        }
    }

    private truncate(str: string, max: number): string {
        if (!str) return "—";
        return str.length > max ? str.substring(0, max) + "…" : str;
    }

    private escapeHtml(str: string): string {
        return str
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;");
    }
}

customElements.define("sessions-element", SessionsElement);

Routing.instance.onChange(async route => {
    if (route.path[0] === "manage" && route.path[1] === "sessions") {
        await AppContextStore.instance.present("", document.createElement("sessions-element"), "Manage", "Login sessions");
    }
});
